import { Fragment, useEffect, useLayoutEffect, useId, useRef, useState } from "react";
import { useMatch, useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Contact, Conversation, Page, Team } from "@autopilot/shared";
import { api, errorMessage, ws } from "../lib/api";
import { useWorkspace } from "../lib/session";
import { Icon, type IconName } from "../ui/Icon";
import { Kbd, useToast } from "../ui/kit";
import * as DialogPrimitive from "@radix-ui/react-dialog";

type Patch = Partial<Pick<Conversation, "assigneeId" | "teamId" | "aiMode" | "status" | "snoozedUntil">>;
type Command = { id: string; label: string; group: string; icon: IconName; run: () => void };
const PAGES: { path: string; label: string; icon: IconName }[] = [
  { path: "inbox?folder=all", label: "All conversations", icon: "inbox" },
  { path: "inbox?folder=mine", label: "Assigned to me", icon: "mine" },
  { path: "inbox?folder=unassigned", label: "Unassigned", icon: "unassigned" },
  { path: "inbox?folder=ai", label: "Ember handled", icon: "ai" },
  { path: "inbox?folder=waiting", label: "Waiting", icon: "clock" },
  { path: "inbox?folder=resolved", label: "Resolved", icon: "check" },
  { path: "ai", label: "Ember", icon: "ai" },
  { path: "knowledge", label: "Knowledge", icon: "book" },
  { path: "customers", label: "Customers", icon: "customers" },
  { path: "analytics", label: "Analytics", icon: "analytics" },
  { path: "automations", label: "Automations", icon: "bolt" },
  { path: "integrations", label: "Integrations", icon: "plug" },
  { path: "settings", label: "Settings", icon: "settings" },
];

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  return <DialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next) onClose(); }}>{open && <OpenPalette onClose={onClose} />}</DialogPrimitive.Root>;
}

function OpenPalette({ onClose }: { onClose: () => void }) {
  const { workspace, me } = useWorkspace();
  const wid = workspace.id;
  const match = useMatch("/app/:wid/inbox/:conversationId");
  const conversationId = match?.params.wid === wid ? match.params.conversationId : undefined;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const id = useId();
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState<"commands" | "teams" | "snooze">("commands");
  const [selected, setSelected] = useState(0);
  const trimmed = query.trim();
  const searching = mode === "commands" && !!search && trimmed === search;

  useLayoutEffect(() => {
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }, []);
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(trimmed), 200);
    return () => window.clearTimeout(timer);
  }, [trimmed]);

  const conversations = useQuery({
    queryKey: ["palette-conversations", wid, search],
    queryFn: () => api.get<Page<Conversation>>(ws(wid, `conversations?${new URLSearchParams({ q: search, status: "open,waiting,snoozed,escalated,resolved", limit: "10" })}`)),
    enabled: searching,
  });
  const contacts = useQuery({
    queryKey: ["palette-contacts", wid, search],
    queryFn: () => api.get<Page<Contact>>(ws(wid, `contacts?${new URLSearchParams({ q: search, limit: "10" })}`)),
    enabled: searching,
  });
  const teams = useQuery({
    queryKey: ["teams", wid],
    queryFn: () => api.get<{ items: Team[] }>(ws(wid, "teams")),
    enabled: mode === "teams",
  });
  const mutation = useMutation({
    mutationFn: ({ patch }: { patch: Patch; message: string }) => api.patch(ws(wid, `conversations/${encodeURIComponent(conversationId!)}`), patch),
    onSuccess: (_data, { message }) => {
      for (const key of ["conversation", "conversations", "counts", "messages", "copilot", "palette-conversations"]) {
        void qc.invalidateQueries({ queryKey: [key, wid] });
      }
      toast(message);
      onClose();
    },
  });
  const changeMode = (next: typeof mode) => { setMode(next); setQuery(""); setSearch(""); setSelected(0); input.current?.focus(); };
  const go = (path: string) => { navigate(`/app/${wid}/${path}`); onClose(); };
  const patch = (value: Patch, message: string) => {
    if (conversationId && !mutation.isPending) mutation.mutate({ patch: value, message });
  };
  let commands: Command[];
  if (mode === "teams") {
    commands = (teams.data?.items ?? []).map((team) => ({ id: team.id, label: team.name, group: "Assign to team", icon: "users", run: () => patch({ teamId: team.id, assigneeId: null, aiMode: "off" }, `Assigned to ${team.name}`) }));
  } else if (mode === "snooze") {
    commands = [
      { label: "For 1 hour", duration: 60 * 60 * 1000 },
      { label: "For 4 hours", duration: 4 * 60 * 60 * 1000 },
      { label: "For 24 hours", duration: 24 * 60 * 60 * 1000 },
    ].map(({ label, duration }) => ({ id: String(duration), label, group: "Snooze conversation", icon: "clock", run: () => patch({ status: "snoozed", snoozedUntil: new Date(Date.now() + duration).toISOString() }, "Conversation snoozed") }));
  } else {
    commands = conversationId ? [
      { id: "assign-me", label: "Assign conversation to me", group: "Current conversation", icon: "user", run: () => patch({ assigneeId: me.user.id, aiMode: "off" }, "Assigned to you") },
      { id: "assign-team", label: "Assign conversation to team…", group: "Current conversation", icon: "users", run: () => changeMode("teams") },
      { id: "assign-ai", label: "Assign conversation to Ember", group: "Current conversation", icon: "ai", run: () => patch({ assigneeId: null, aiMode: "auto" }, "Assigned to Ember") },
      { id: "resolve", label: "Resolve conversation", group: "Current conversation", icon: "check", run: () => patch({ status: "resolved", snoozedUntil: null }, "Conversation resolved") },
      { id: "snooze", label: "Snooze conversation…", group: "Current conversation", icon: "clock", run: () => changeMode("snooze") },
    ] : [];
    commands.push(...PAGES.map((page): Command => ({ id: page.path, label: `Go to ${page.label}`, group: "Navigation", icon: page.icon, run: () => go(page.path) })));
  }
  commands = commands.filter((command) => command.label.toLowerCase().includes(trimmed.toLowerCase()));
  if (searching) {
    commands.push(...(conversations.data?.items ?? []).map((conversation): Command => ({
      id: `conversation-${conversation.id}`, label: `#${conversation.number} ${conversation.subject} · ${conversation.contact.name ?? conversation.contact.email ?? "Visitor"}`,
      group: "Conversations", icon: "chat", run: () => go(`inbox/${encodeURIComponent(conversation.id)}`),
    })));
    commands.push(...(contacts.data?.items ?? []).map((contact): Command => ({
      id: `contact-${contact.id}`, label: [contact.name, contact.email, contact.company].filter(Boolean).join(" · ") || "Unnamed contact",
      group: "Contacts", icon: "user", run: () => go(`customers/${encodeURIComponent(contact.id)}`),
    })));
  }
  const active = Math.min(selected, Math.max(0, commands.length - 1));
  const activeId = commands[active]?.id;
  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [active, activeId]);
  const loading = mode === "teams" ? teams.isPending : mode === "commands" && !!trimmed && (trimmed !== search || conversations.isFetching || contacts.isFetching);
  const errors = [mutation.error, mode === "teams" ? teams.error : null, searching ? conversations.error : null, searching ? contacts.error : null].filter(Boolean);

  return (
    <DialogPrimitive.Portal container={document.getElementById("ap-root")}>
      <DialogPrimitive.Overlay className="scrim radix-scrim" />
      <DialogPrimitive.Content className="pal radix-dialog" style={{ maxWidth: 560, top: "18vh", transform: "translateX(-50%)" }} aria-label="Search and commands" aria-describedby={undefined} aria-busy={mutation.isPending} onOpenAutoFocus={(event) => { event.preventDefault(); input.current?.focus(); }} onCloseAutoFocus={(event) => { event.preventDefault(); returnFocus.current?.focus(); }} onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === "Escape") { event.preventDefault(); onClose(); }
        else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          if (commands.length) setSelected((active + (event.key === "ArrowDown" ? 1 : -1) + commands.length) % commands.length);
        } else if (event.key === "Enter" && !event.nativeEvent.isComposing) {
          event.preventDefault();
          if (!mutation.isPending) commands[active]?.run();
        }
      }}>
        <DialogPrimitive.Title className="visually-hidden">Search Hearthdesk</DialogPrimitive.Title>
        <div className="pin">
          {mode !== "commands" ? <button type="button" className="btn ghost icon sm" tabIndex={-1} aria-label="Back to commands" onClick={() => changeMode("commands")}><Icon name="chevronLeft" /></button> : <Icon name="search" />}
          <input ref={input} role="combobox" aria-label={mode === "teams" ? "Search teams" : "Search conversations, contacts and commands"} aria-expanded="true" aria-autocomplete="list" aria-controls={`${id}-list`} aria-activedescendant={activeId ? `${id}-option-${active}` : undefined} placeholder={mode === "teams" ? "Choose a team…" : mode === "snooze" ? "Choose snooze duration…" : "Search or jump to…"} value={query} onChange={(event) => { setQuery(event.target.value); setSelected(0); }} />
          <button type="button" className="btn ghost sm" tabIndex={-1} aria-label="Close command palette" onClick={onClose}><Kbd>Esc</Kbd></button>
        </div>
        <div className="pl" ref={list}>
          <div id={`${id}-list`} role="listbox" aria-label="Commands and search results">
            {commands.map((command, index) => <Fragment key={command.id}>
              {commands[index - 1]?.group !== command.group && <div className="pg" role="presentation">{command.group}</div>}
              <button type="button" id={`${id}-option-${index}`} role="option" aria-selected={index === active} disabled={mutation.isPending} tabIndex={-1} className="po" onMouseMove={() => setSelected(index)} onMouseDown={(event) => event.preventDefault()} onClick={command.run}>
                <Icon name={command.icon} size="sm" /><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{command.label}</span>
              </button>
            </Fragment>)}
          </div>
          <div role="status" className="pg">{mutation.isPending ? "Updating conversation…" : loading ? "Searching…" : !commands.length ? mode === "teams" ? "No teams found." : "No matching commands or results." : searching ? `${conversations.data?.items.length ?? 0} conversations and ${contacts.data?.items.length ?? 0} contacts` : null}</div>
          {errors.map((error, index) => <div className="err" role="alert" key={index}>{errorMessage(error)}</div>)}
        </div>
        <div className="pf"><span><Kbd>↑ ↓</Kbd> Navigate</span><span><Kbd>Enter</Kbd> Select</span><span><Kbd>Esc</Kbd> Close</span></div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
